import firebase from "firebase/compat/app";
import { firestore } from "../firebaseConfig";
import { Timestamp, IVehicle } from "../types";
import { query } from "firebase/firestore";
import { getApiToken } from "../data/api/token";
import {
  createRegisteredVehicle as createRegisteredVehicleApi,
  updateVehicle as updateVehicleApi,
  deleteVehicle as deleteVehicleApi,
} from "../data/api/vehicles";

type DocumentSnapshot = firebase.firestore.DocumentSnapshot;
type SnapshotOptions = firebase.firestore.SnapshotOptions;

class Vehicle implements IVehicle {
  id: string;
  workspaceId: string;
  companyId: string;
  name: string;
  registrationNumber: string;
  maxWeight: number;
  vehicleMake?: string | null;
  vehicleModel?: string | null;
  vehicleTypeDesignation?: string | null;
  vin?: string | null;
  curbWeightKg?: number | null;
  payloadCapacityKg?: number | null;
  permittedTotalWeightKg?: number | null;
  lengthCm?: number | null;
  widthCm?: number | null;
  heightCm?: number | null;
  fuelTypes?: string[];
  co2EmissionsGKm?: number | null;
  co2EmissionsSource?: string | null;
  co2MeasurementMethod?: string | null;
  fuelConsumptionLPer100Km?: number | null;
  electricEnergyConsumptionWhPerKm?: number | null;
  electricRangeKm?: number | null;
  euroClass?: string | null;
  nextInspectionDate?: string | null;
  vehicleStatus?: string | null;
  palletSpaces?: number | null;
  cargoVolumeM3?: number | null;
  specialEquipment?: string[];
  registrySource?: string | null;
  registryFetchedAt?: Timestamp;
  categoryCode?: string | null;
  createdAt?: Timestamp;

  static collectionName = "vehicles";

  static converter = {
    toFirestore(vehicle: Vehicle) {
      return vehicle.data();
    },
    fromFirestore(snapshot: DocumentSnapshot, options: SnapshotOptions) {
      const data = snapshot.data(options) as IVehicle;
      return new Vehicle({ ...data, id: snapshot.id });
    },
  };

  static createId() {
    return firestore.collection(Vehicle.collectionName).doc().id;
  }

  constructor({
    id,
    workspaceId,
    companyId,
    name,
    registrationNumber,
    maxWeight,
    categoryCode,
    createdAt,
    ...details
  }: IVehicle) {
    this.id = id;
    this.workspaceId = workspaceId;
    this.companyId = companyId;
    this.name = name;
    this.registrationNumber = registrationNumber;
    this.maxWeight = maxWeight;
    this.categoryCode = categoryCode || null;
    this.createdAt = createdAt;
    Object.assign(this, details);
  }

  clone() {
    return Object.assign(Object.create(Object.getPrototypeOf(this)), this);
  }

  setData(updates: Partial<IVehicle>) {
    return Object.assign(this, updates);
  }

  data() {
    return {
      id: this.id,
      workspaceId: this.workspaceId,
      companyId: this.companyId,
      name: this.name,
      registrationNumber: this.registrationNumber || null,
      maxWeight: this.maxWeight || null,
      vehicleMake: this.vehicleMake || null,
      vehicleModel: this.vehicleModel || null,
      vehicleTypeDesignation: this.vehicleTypeDesignation || null,
      vin: this.vin || null,
      curbWeightKg: this.curbWeightKg ?? null,
      payloadCapacityKg: this.payloadCapacityKg ?? null,
      permittedTotalWeightKg:
        this.permittedTotalWeightKg ?? this.maxWeight ?? null,
      lengthCm: this.lengthCm ?? null,
      widthCm: this.widthCm ?? null,
      heightCm: this.heightCm ?? null,
      fuelTypes: this.fuelTypes ?? [],
      co2EmissionsGKm: this.co2EmissionsGKm ?? null,
      co2EmissionsSource: this.co2EmissionsSource || null,
      co2MeasurementMethod: this.co2MeasurementMethod || null,
      fuelConsumptionLPer100Km: this.fuelConsumptionLPer100Km ?? null,
      electricEnergyConsumptionWhPerKm:
        this.electricEnergyConsumptionWhPerKm ?? null,
      electricRangeKm: this.electricRangeKm ?? null,
      euroClass: this.euroClass || null,
      nextInspectionDate: this.nextInspectionDate || null,
      vehicleStatus: this.vehicleStatus || null,
      palletSpaces: this.palletSpaces ?? null,
      cargoVolumeM3: this.cargoVolumeM3 ?? null,
      specialEquipment: this.specialEquipment ?? [],
      registrySource: this.registrySource || null,
      registryFetchedAt: this.registryFetchedAt || null,
      categoryCode: this.categoryCode || null,
      createdAt: this.createdAt || null,
    };
  }

  static default() {
    return new Vehicle({
      id: "",
      workspaceId: "",
      companyId: "",
      name: "",
      maxWeight: 3500,
      registrationNumber: "",
    });
  }

  static isNew(data: IVehicle): boolean {
    return data.id.length === 0;
  }

  static create = async (
    workspaceId: string,
    data: Omit<IVehicle, "id" | "createdAt">,
  ) => {
    const newVehicle = new Vehicle({
      ...data,
      workspaceId: workspaceId,
      id: "",
    });
    try {
      const token = await getApiToken();
      await createRegisteredVehicleApi(token, workspaceId, {
        ...newVehicle.data(),
        lookupRegistry: newVehicle.registrySource === "SVV",
      });
    } catch (e) {
      console.warn("Create vehicle failed with error: ", e);
      return false;
    }
    return true;
  };

  static list = (workspaceId: string) => {
    return query(
      firestore
        .collection(Vehicle.collectionName)
        .where("workspaceId", "==", workspaceId)
        .withConverter(Vehicle.converter),
    );
  };

  static update = async (vehicle: Vehicle, updates: Partial<IVehicle>) => {
    const updateType =
      (vehicle.clone && vehicle.clone()) || new Vehicle({ ...vehicle });

    updateType.setData({
      ...updates,
    });

    const token = await getApiToken();
    const updated = await updateVehicleApi(
      token,
      updateType.workspaceId,
      vehicle.id,
      updateType.data(),
    );

    return new Vehicle(updated);
  };

  static remove = async (vehicle: Vehicle) => {
    const token = await getApiToken();
    return await deleteVehicleApi(token, vehicle.workspaceId, vehicle.id);
  };
}

export default Vehicle;
